import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Box, Fade, Stack, Typography, useTheme } from "@mui/material";
import { getBitacora } from "../../../../apis";
import { TableSkeleton } from "../../../Loading/Table/Table";
import { EGPaginator } from "../../../Paginator/Paginator";
import { BitacoraTable } from "../../../MainComponents/Bitacora/BitacoraTable/BitacoraTable";
import { EGFlagMessage } from "../../../MainComponents/FlagMessage/EGFlagMessage";

/**
 * ReportBitacoraTab
 * Pestaña de Bitácora dentro de un reporte. Reutiliza la misma BitacoraTable de
 * la pantalla global para que ambas se vean y se mantengan igual; lo único que
 * cambia es que aquí la consulta va filtrada al reporte abierto.
 *
 * Se monta con `mountOnEnter` desde ReportViewer, así que este componente (y su
 * petición) no existen hasta que el usuario entra a la pestaña.
 */
export const ReportBitacoraTab = ({ reportId }) => {
  const theme = useTheme();
  const { t } = useTranslation("report");

  // ---> Initial states <--- //
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [bitacoraList, setBitacoraList] = useState([]);
  // Server pagination state
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Calculation of total pages
  const totalPages = Math.max(1, Math.ceil(totalCount / rowsPerPage));

  // API call filtered by report
  const fetchBitacora = useCallback(
    async (pageParam = page, sizeParam = rowsPerPage) => {
      if (!reportId) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setIsError(false);
      try {
        // Solo report_id: el folio no es único (los reportes sin folio válido
        // comparten "ERR-None"), así que filtrar por él traería actividad de
        // otros reportes.
        const params = {
          report_id: reportId,
          page: pageParam,
          page_size: sizeParam,
        };

        const data = await getBitacora(params);

        setBitacoraList(data?.results ?? []);
        setTotalCount(data?.count ?? 0);
      } catch (error) {
        console.error("[ReportBitacoraTab] fetch error:", error);
        setIsError(true);
      } finally {
        setIsLoading(false);
      }
    },
    [reportId]
  );

  // Fetch when report, page or rowsPerPage changes
  useEffect(() => {
    fetchBitacora(page, rowsPerPage);
  }, [fetchBitacora, page, rowsPerPage]);

  // Page onChange event
  const handlePageChange = (_evt, newPage) => {
    setPage(newPage);
  };

  // Rows per page onChange event
  const handleRowsPerPageChange = (evt) => {
    const newSize = Number(evt.target.value);
    setRowsPerPage(newSize);
    setPage(1); // Reset to first page
  };

  // Don't render if no report is provided
  if (!reportId) {
    return null;
  }

  return (
    <Box className="mt-20 dyTheme1 dyBorder1 rounded">
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          bgcolor: theme.palette.blueGray50.main,
          py: 1.5,
          px: 2,
          borderTopLeftRadius: 6,
          borderTopRightRadius: 6,
        }}
      >
        <Typography variant="h6" color="secondary">
          {t("Bitacora.title")}
        </Typography>
        <Typography variant="body2" color="secondary">
          {`${t("ReportsList.showing")}: ${totalCount} ${t("Bitacora.result", {
            count: totalCount,
          })}`}
        </Typography>
      </Box>

      {/* Table + Paginator */}
      <Box sx={{ p: 1.5 }}>
        {isLoading ? (
          <TableSkeleton />
        ) : isError ? (
          <EGFlagMessage
            exists
            type="danger"
            message={t("Bitacora.fetch_error")}
          />
        ) : (
          <Fade in timeout={1000}>
            <Box>
              <BitacoraTable bitacoraList={bitacoraList} />

              {totalCount > 0 && (
                <Stack
                  component="nav"
                  sx={{
                    borderTop: `1px solid ${theme.palette.blueGray50.main}`,
                    alignItems: "center",
                    justifyContent: "space-around",
                    flexFlow: "row wrap",
                    py: 2,
                    mt: 2,
                  }}
                  spacing={1}
                >
                  <Stack sx={{ flexFlow: "row nowrap" }}>
                    <Typography p={0.5} fontSize={11}>
                      {t("ReportsList.per_page")}:
                    </Typography>
                    <select
                      className="paginator__select"
                      name="limit"
                      value={rowsPerPage}
                      onChange={handleRowsPerPageChange}
                    >
                      <option value="10">10</option>
                      <option value="15">15</option>
                      <option value="20">20</option>
                    </select>
                  </Stack>

                  <Stack
                    direction="row"
                    justifyContent="center"
                    alignItems="center"
                  >
                    <EGPaginator
                      count={totalPages}
                      page={page}
                      onChange={handlePageChange}
                    />
                  </Stack>

                  <Typography p={0.5} fontSize={12}>
                    {`${t("ReportsList.results")}: ${
                      totalCount === 0 ? 0 : (page - 1) * rowsPerPage + 1
                    } ${t("ReportsList.to")} ${Math.min(
                      page * rowsPerPage,
                      totalCount
                    )} ${t("ReportsList.of")} ${totalCount}`}
                  </Typography>
                </Stack>
              )}
            </Box>
          </Fade>
        )}
      </Box>
    </Box>
  );
};
